import { useState } from "haunted";
import { html, TemplateResult } from "lit-html";
import classNames from "classnames";
import { isTabletOrMobileResolution } from "Services/common";
import { useEffectWithCleanup } from "Shared/haunted/CustomHooks";
import { PUB_SUBS } from "Services/pub-sub-service/PubSub";

interface Props {
	title: string;
	content: TemplateResult;
	onClose?: () => void;
}

let scrollLockCount = 0;
let previousBodyOverflow = "";
let previousHtmlOverflow = "";

const lockBodyScroll = () => {
	const body = document.body;
	const htmlElement = document.documentElement;

	if (!body || !htmlElement) return;

	if (scrollLockCount === 0) {
		previousBodyOverflow = body.style.overflow || undefined;
		previousHtmlOverflow = htmlElement.style.overflow || undefined;

		body.style.overflow = "hidden";
		htmlElement.style.overflow = "hidden";
	}

	scrollLockCount += 1;
};

const unlockBodyScroll = () => {
	const body = document.body;
	const htmlElement = document.documentElement;

	if (!body || !htmlElement || scrollLockCount === 0) return;

	scrollLockCount = Math.max(scrollLockCount - 1, 0);

	if (scrollLockCount === 0) {
		body.style.overflow = previousBodyOverflow ?? "";
		htmlElement.style.overflow = previousHtmlOverflow ?? "";

		previousBodyOverflow = null;
		previousHtmlOverflow = null;
	}
};

export const useMobileSearchboxModal = (props: Props) => {
	const [isOpen, setIsOpen] = useState(false);
	const [shouldRender, setShouldRender] = useState(false);

	const open = () => {
		setShouldRender(true);
		window.setTimeout(() => setIsOpen(true), 10);
	};

	const close = (e: MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();

		setIsOpen(false);
		window.setTimeout(() => {
			setShouldRender(false);
			props.onClose?.();
		}, 300);
		PUB_SUBS.MobileSearchboxModalClosed.publish({});
	};

	useEffectWithCleanup(() => {
		if (isOpen) lockBodyScroll();

		return () => unlockBodyScroll();
	}, [isOpen]);

	const headerTemplate = () => html`
		<div class="relative mb-8 flex items-center justify-between">
			<div class="text-xl font-bold text-n-cyan">${props.title}</div>
			<div
				class="absolute right-0 z-10 flex h-7 w-7 cursor-pointer items-center justify-center pb-0.5 pl-px text-[22px] font-semibold leading-none text-[rgba(51,84,109,0.7)] vertical-center sm:right-3 sm:top-3 sm:h-8 sm:w-8 sm:pl-px sm:text-[26px]"
				data-test-id="modal-close-button"
				@click=${(e: MouseEvent) => close(e)}
			>
				&times;
			</div>
		</div>
	`;

	const htmlTemplate = () =>
		isTabletOrMobileResolution() && shouldRender
			? html`
					<div
						class=${classNames(
							"fixed left-0 top-0 z-[11111111] flex h-screen w-screen items-center justify-center bg-white transform transition-transform duration-300 ease-in-out",
							{ "translate-x-0": isOpen, "translate-x-full": !isOpen }
						)}
					>
						<div class="flex h-full w-full flex-col px-4 pb-8 pt-6">
							${headerTemplate()} ${props.content}
						</div>
					</div>
			  `
			: html``;

	return {
		htmlTemplate,
		open,
		close,
		isOpen,
	};
};
